import React, { useState, useContext, useEffect, useMemo, useRef } from "react";
import { TableContext } from "../../context";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import {
  McTable,
  McButton,
  McPagination,
  McTabBar,
  McTab,
  McTooltip,
} from "@maersk-global/mds-react-wrapper";
import { McModal } from "@maersk-global/mds-react-wrapper/components-core/mc-modal";
import { McPopover } from "@maersk-global/mds-react-wrapper/components-core/mc-popover";
import { McCheckboxGroup } from "@maersk-global/mds-react-wrapper/components-core/mc-checkbox-group";
import { McCheckbox } from "@maersk-global/mds-react-wrapper/components-core/mc-checkbox";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import "../../App.css";

import AnalyticsSection from "./AnalyticsSection";
import ApplicationFormFields from "./ApplicationFormFields";
import {
  CHANNEL_TAB_LABELS,
  RECORDS_PER_PAGE,
  getChannelTableColumns,
} from "./constants";
import {
  filterChannelData,
  transformChannelData,
  downloadCSV,
  formatDataForDownload,
  transformPlatformAnalytics,
  transformPortfolioAnalytics,
  getPortfoliosForPlatform,
  getPlatformsForPortfolio,
  sortArrayByProperty,
} from "./utils";
import {
  fetchAppDetailsByAppId,
  fetchAppDetailsByAppName,
  fetchIncidentDetails,
  submitNewApplication,
  updateApplication,
  validateMandatoryFields,
  fetchStatisticsData,
} from "./apiHelpers";

import axios from "axios";

// Import modal components 
import StatisticsModal from "./StatisticsModal";
import DocumentUploadModal from "./DocumentUploadModal";

/**
 * RedGreenChannelTracker component for displaying analytics and channel tracking data.
 * Shows platform and portfolio analytics at the top, followed by Red/Green/All channel tables.
 */
export default function RedGreenChannelTracker({
  showAnalytics = true,
  addButtonLabel = "Add to Red Channel",
}) {
  const {
    redGreenAnalytics,
    redGreenChannelData,
    getRedGreenAnalytics,
    getRedGreenChannelData,
    tokenFetched,
    graphData,
    userRole,
  } = useContext(TableContext);

  const [activeChannelTab, setActiveChannelTab] = useState("Red Channel");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState("added_to_red_channel");
  const [sortDirection, setSortDirection] = useState("ascending");

  // Column filters state
  const [platformColumnFilters, setPlatformColumnFilters] = useState({});
  const [portfolioColumnFilters, setPortfolioColumnFilters] = useState({});
  const [channelColumnFilters, setChannelColumnFilters] = useState({});

  // Row selection state for bidirectional filtering
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);

  // Add/Edit Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [originalAppName, setOriginalAppName] = useState("");
  const [originalIncidentId, setOriginalIncidentId] = useState("");
  const [addValidationError, setAddValidationError] = useState("");
  const [editValidationError, setEditValidationError] = useState("");
  const [addScrollTrigger, setAddScrollTrigger] = useState(0);
  const [editScrollTrigger, setEditScrollTrigger] = useState(0);

  // Form State
  const [formData, setFormData] = useState({
    app_id: "",
    app_name: "",
    platform: "",
    portfolio: "",
    status: "Red",
    trigger_type: "",
    incident: "",
    priority: "",
    incident_dates: "",
    added_to_red_channel: DateTime.now(),
    moved_to_green_channel_on: null,
    final_review: DateTime.now().plus({ months: 3 }),
    deployment_type: "",
    deployment_details: "",
    comments: "",
  });

  const [editFormData, setEditFormData] = useState({});
  const [appNameOptions, setAppNameOptions] = useState([]);
  const [editAppNameOptions, setEditAppNameOptions] = useState([]);
  const appNameTypeaheadRef = useRef(null);
  const editAppNameTypeaheadRef = useRef(null);
  const addErrorRef = useRef(null);
  const editErrorRef = useRef(null);

  // Track manually set dates
  const manuallySetDatesRef = useRef({
    final_review: false,
    moved_to_green_channel_on: false,
  });

  const editManuallySetDatesRef = useRef({
    final_review: false,
    moved_to_green_channel_on: false,
  });

  // Debounce timers
  const appIdTimerRef = useRef(null);
  const appNameTimerRef = useRef(null);
  const incidentTimerRef = useRef(null);
  const editAppIdTimerRef = useRef(null);
  const editAppNameTimerRef = useRef(null);
  const editIncidentTimerRef = useRef(null);

  // Statistics Modal State
  const [isStatisticsModalOpen, setIsStatisticsModalOpen] = useState(false);
  const [statisticsData, setStatisticsData] = useState(null);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [postmortemPreventiveMeasures, setPostmortemPreventiveMeasures] = useState([]);
  const [selectedRowForStats, setSelectedRowForStats] = useState(null);
  const [incidentCounts, setIncidentCounts] = useState(null);
  const [oldestIncidentDate, setOldestIncidentDate] = useState(null);

  // Delete Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Email Modal State
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailFormData, setEmailFormData] = useState({
    recipients: [],
    subject: "",
    body: "",
    includeAttachment: true, // Default to checked
  });
  const [selectedRowForEmail, setSelectedRowForEmail] = useState(null);
  const [isLoadingOwners, setIsLoadingOwners] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const emailBodyTextareaRef = useRef(null);

  // Document Upload Modal State
  const [isDocumentUploadModalOpen, setIsDocumentUploadModalOpen] = useState(false);
  const [selectedRowForUpload, setSelectedRowForUpload] = useState(null);

  const darkPrimaryTheme = createTheme({
    palette: {
      primary: {
        main: "#141414",
      },
    },
  });

  useEffect(() => {
    if (tokenFetched) {
      loadData();
    }
  }, [tokenFetched]);

  useEffect(() => {
    setCurrentPage(1);
    setChannelColumnFilters({}); // Reset channel filters when tab changes
  }, [activeChannelTab]);

  // Auto-scroll to error message in Add modal
  useEffect(() => {
    if (addValidationError && addErrorRef.current && addScrollTrigger > 0) {
      setTimeout(() => {
        addErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  }, [addScrollTrigger]);

  // Auto-scroll to error message in Edit modal
  useEffect(() => {
    if (editValidationError && editErrorRef.current && editScrollTrigger > 0) {
      setTimeout(() => {
        editErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  }, [editScrollTrigger]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([getRedGreenAnalytics(), getRedGreenChannelData()]);
    setLoading(false);
  };

  const handleRefresh = () => {
    loadData();
  };

  const handleChannelTabChange = (e) => {
    const tabIndex = e.detail;
    const newTabLabel = CHANNEL_TAB_LABELS[tabIndex];
    setActiveChannelTab(newTabLabel);
  };

  const getChannelTabIndex = () => {
    return CHANNEL_TAB_LABELS.indexOf(activeChannelTab);
  };

  // Download handlers
  const handleDownloadPlatformAnalytics = () => {
    // Get the appropriate data based on selection
    let rawData = selectedPortfolio 
      ? getPlatformsForPortfolio(redGreenAnalytics, selectedPortfolio)
      : transformPlatformAnalytics(redGreenAnalytics);
    
    // Apply column filters
    const filteredData = applyColumnFilters(rawData, platformColumnFilters);
    
    // Sort by red_channel descending (same as table default)
    filteredData.sort((a, b) => b.red_channel - a.red_channel);
    
    // Format for CSV
    const platformData = filteredData.map((item) => ({
      Platform: String(item.platform || "N/A"),
      "Total Prod App Instances": Number(item.total_apps) || 0,
      "Red Channel": Number(item.red_channel) || 0,
      "Green Channel": Number(item.green_channel) || 0,
      "Red %": Number(item.red_percentage) || 0,
    }));
    
    downloadCSV(platformData, `Platform_Analytics_${DateTime.now().toFormat("yyyy-MM-dd_HH-mm-ss")}.csv`);
  };

  const handleDownloadPortfolioAnalytics = () => {
    // Get the appropriate data based on selection
    let rawData = selectedPlatform
      ? getPortfoliosForPlatform(redGreenAnalytics, selectedPlatform)
      : transformPortfolioAnalytics(redGreenAnalytics);
    
    // Apply column filters
    const filteredData = applyColumnFilters(rawData, portfolioColumnFilters);
    
    // Sort by red_channel descending (same as table default)
    filteredData.sort((a, b) => b.red_channel - a.red_channel);
    
    // Format for CSV
    const portfolioData = filteredData.map((item) => ({
      Portfolio: String(item.portfolio || "N/A"),
      "Total Prod App Instances": Number(item.total_apps) || 0,
      "Red Channel": Number(item.red_channel) || 0,
      "Green Channel": Number(item.green_channel) || 0,
      "Red %": Number(item.red_percentage) || 0,
    }));
    
    downloadCSV(portfolioData, `Portfolio_Analytics_${DateTime.now().toFormat("yyyy-MM-dd_HH-mm-ss")}.csv`);
  };

  const handleDownloadClick = () => {
    const data = formatDataForDownload(filteredChannelData, activeChannelTab);
    downloadCSV(data, `RedGreen_Channel_${activeChannelTab}_${DateTime.now().toFormat("yyyy-MM-dd_HH-mm-ss")}.csv`);
  };

  // Statistics Modal handlers
  const handleOpenStatisticsModal = async (row) => {
    setSelectedRowForStats(row);
    setIsStatisticsModalOpen(true);
    setStatisticsLoading(true);
    try {
      const { statisticsData: stats, incidentCounts: counts, postmortemPreventiveMeasures: measures, oldestIncidentDate: oldestDate } = await fetchStatisticsData(
        row.app_id,
        row.app_name,
        row.added_to_red_channel,
        row.latest_incident
      );
      setStatisticsData(stats);
      setIncidentCounts(counts);
      setPostmortemPreventiveMeasures(measures || []);
      setOldestIncidentDate(oldestDate || null);
    } catch (error) {
      setStatisticsData(null);
      setIncidentCounts(null);
      setPostmortemPreventiveMeasures([]);
      setOldestIncidentDate(null);
    } finally {
      setStatisticsLoading(false);
    }
  };

  const handleCloseStatisticsModal = () => {
    setIsStatisticsModalOpen(false);
    setSelectedRowForStats(null);
    setStatisticsData(null);
    setIncidentCounts(null);
    setPostmortemPreventiveMeasures([]);
    setOldestIncidentDate(null);
  };

 

  // Delete Modal handlers
  const handleOpenDeleteModal = (row) => {
    setRecordToDelete(row);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setRecordToDelete(null);
  };

  // Email Modal handlers
  const handleOpenEmailModal = async (row) => {
    setSelectedRowForEmail(row);
    setIsEmailModalOpen(true);
    setIsLoadingOwners(true);

    try {
      const isMovedToGreen = activeChannelTab === "Moved to Green Channel";
      const isRedChannel = activeChannelTab === "Red Channel";
      
      let recipients = "";
      let incidentShortDesc = "";
      let incidentCIName = "";

      // Fetch app owners and incident details in one call
      if (isRedChannel && row.latest_incident) {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URI}/api/redChannelEmailDetails?app_name=${encodeURIComponent(row.app_name)}&incident_id=${encodeURIComponent(row.latest_incident)}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
              },
            }
          );

          if (response.data && response.data.status === "success") {
            recipients = response.data.app_owners ? response.data.app_owners.join(", ") : "";
            if (response.data.incident_details) {
              incidentShortDesc = response.data.incident_details.short_description || "";
              incidentCIName = response.data.incident_details.configuration_item || "";
            }
          }
        } catch (error) {
          console.error("Error fetching red channel email details:", error);
          toast.error(error.response?.data?.message || "Failed to fetch email details", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      } else {
        // For non-red channel tabs, just fetch app owners
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URI}/api/appOwners?app_name=${encodeURIComponent(row.app_name)}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
              },
            }
          );

          if (response.data && response.data.owners) {
            recipients = response.data.owners.join(", ");
          }
        } catch (error) {
          console.error("Error fetching app owners:", error);
          toast.error(error.response?.data?.message || "Failed to fetch app owners", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      }

      let subject = "";
      let body = "";

      if (isMovedToGreen) {
        subject = `${row.app_name} - Moved to Green Channel`;
        body = `Dear Team,\n\nWe are pleased to inform you that the application " ${row.app_id} - ${row.app_name}" has been successfully moved to the Green Channel.\n\nBest regards,\nTechnology Change Management Team`;
      } else if (isRedChannel) {
        const deploymentType = row.deployment_type || "";
        const isManualDeployment = deploymentType.toLowerCase() === "manual";
        const addedDate = row.added_to_red_channel 
          ? DateTime.fromISO(row.added_to_red_channel).toFormat("dd-MMM-yyyy")
          : DateTime.now().toFormat("dd-MMM-yyyy");

        if (isManualDeployment) {
          // Manual deployment template
          subject = `Red Channel Onboarding - ${row.app_id} - ${row.app_name}`;
          body = `Hello,

As part of Red-Green Channel Framework, we are writing to inform you that the app ${row.app_id}-${row.app_name} have been onboarded to the Red Channel.

Red Channel Tracker: https://flux.maersk.io/RedGreenChannelTracker

This onboarding has been initiated due to the occurrence of recent Major Incident, which necessitate increased governance, detailed change scrutiny, and closer operational monitoring to ensure improved stability and resilience.

Reason for Red Channel Placement:
• Major Incident ${row.latest_incident || ""}:  ${incidentShortDesc || ""}. CI Name: ${incidentCIName || ""}

As per the Red Channel guidelines, all upcoming change activities for the above applications will warrant additional approval from corresponding application director and will undergo enhanced review for next 3 months, including validation of test evidence, rollback readiness prior to approval and deployment.

We appreciate your support and collaboration in driving stability and governance maturity across these critical applications.

Please feel free to reach out to the Technology Change <technology.change@maersk.com> for any clarifications or discussions on next steps.

Best Regards,
Technology Change Management Team`;
        } else {
          // Automated deployment template
          subject = `Red Channel Placement - ${row.app_id} – ${row.app_name}`;
          body = `Hello,

As part of our Red-Green Channel Framework to ensure safe and reliable production changes/deployments, your application '${row.app_id} – ${row.app_name}' has been placed in the Red Channel effective ${addedDate}

Reason for Red Channel Placement:
• Major Incident ${row.latest_incident || ""}:  ${incidentShortDesc || ""}. CI Name: ${incidentCIName || ""}

What This Means for Your Application:
• All changes to production should follow 'Normal Change Management Process' in ServiceNow
• All changes will require manual approval from your engineering manager and Global Change Management Team
• Mandatory pre-deployment testing and sign-off will be enforced as a pre-requisite for approval
• Post-deployment monitoring and validation will be required for every release.

Minimum Duration: Your application will remain in the Red Channel for a minimum of 3 months from the date of this notice. If another trigger occurs during this period, the 3-month timer will reset.

Steps to Return to Green Channel: To transition back to the Green Channel, the following conditions must be met:
• No major incidents during the Red Channel period
• Automated tests, rollback strategies, and validated pre-deployment testing in lower environments
• App certified in MSA and GitHub Repos mapped
• Approval from Change Management and SRE leads

Note: In an effort to making the approval process smoother and easier, integrate your GitHub workflow with Gatekeeper and trigger approvals directly from your pipeline.

For detailed guidelines, please refer to the Change Control Framework here and FAQs available here. Process communication to directors and LT attached.

If you need assistance or guidance to meet these requirements, our team is available to support you. Please reach out to technology.change@maersk.com for any queries. Thank you for your cooperation in maintaining operational stability and deployment quality.

Best Regards,
Technology Change Management Team`;
        }
      } else {
        // Default template for other tabs
        subject = `${row.app_name} - Channel Update`;
        body = `Dear Team,\n\nThis is to inform you about an update to the application "${row.app_name}".\n\nApp ID: ${row.app_id}\nPlatform: ${row.platform}\nPortfolio: ${row.portfolio}\n\nBest regards,\nChange Management Team`;
      }

      setEmailFormData({
        recipients,
        subject,
        body,
        includeAttachment: true, // Default to checked for automated deployments
      });
    } catch (error) {
      console.error("Error in email modal:", error);
      toast.error(error.response?.data?.message || "Failed to load email details", {
        position: "top-right",
        autoClose: 3000,
      });
      setEmailFormData({
        recipients: "",
        subject: "",
        body: "",
      });
    } finally {
      setIsLoadingOwners(false);
    }
  };

  const handleCloseEmailModal = () => {
    setIsEmailModalOpen(false);
    setSelectedRowForEmail(null);
    setEmailFormData({
      recipients: "",
      subject: "",
      body: "",
      includeAttachment: true,
    });
  };

  // Document Upload Modal handlers
  const handleOpenDocumentUploadModal = (row) => {
    setSelectedRowForUpload(row);
    setIsDocumentUploadModalOpen(true);
  };

  const handleCloseDocumentUploadModal = () => {
    setIsDocumentUploadModalOpen(false);
    setSelectedRowForUpload(null);
  };

  const handleDocumentUploadSuccess = () => {
    // Optionally refresh data or show success notification
    // The upload modal already shows a toast notification
  };

  const handleEmailFormChange = (field, value) => {
    setEmailFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const handleSendEmail = async () => {
    // Validate recipients
    const recipientsArray = emailFormData.recipients
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    if (recipientsArray.length === 0) {
      toast.error("Please enter at least one recipient email address", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Validate email format
    const invalidEmails = recipientsArray.filter((email) => !validateEmail(email));
    if (invalidEmails.length > 0) {
      toast.error(`Invalid email format: ${invalidEmails.join(", ")}`, {
        position: "top-right",
        autoClose: 4000,
      });
      return;
    }

    // Trim empty lines from body
    const trimmedBody = emailFormData.body.trim();

    if (!trimmedBody) {
      toast.error("Email body cannot be empty", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsSendingEmail(true);

    try {
      // Convert plain text body to HTML with hyperlinks
      let htmlBody = trimmedBody.replace(/\n/g, '<br>');
      htmlBody = htmlBody.replace(
        'Red Channel Tracker: https://flux.maersk.io/RedGreenChannelTracker',
        'Red Channel Tracker: <a href="https://flux.maersk.io/RedGreenChannelTracker">https://flux.maersk.io/RedGreenChannelTracker</a>'
      );
      const htmlContent = `<html><body style="font-family: Arial, sans-serif;">${htmlBody}</body></html>`;

      const requestBody = {
        recipients: recipientsArray,
        subject: emailFormData.subject,
        content: htmlContent,
      };

      // Add includeAttachment to request body if checked and deployment is automated
      const isAutomatedDeployment = selectedRowForEmail?.deployment_type?.toLowerCase() === "automated";
      if (emailFormData.includeAttachment && isAutomatedDeployment) {
        requestBody.includeAttachment = true;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URI}/api/sendEmail`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && response.data.success) {
        toast.success(response.data.message || "Email sent successfully", {
          position: "top-right",
          autoClose: 3000,
        });
        handleCloseEmailModal();
      } else {
        toast.error(response.data?.message || "Failed to send email", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error(error.response?.data?.message || "Failed to send email", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!recordToDelete) return;

    setIsDeleting(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URI}/api/redGreenChannel?app_name=${encodeURIComponent(recordToDelete.app_name)}&incident_id=${recordToDelete.latest_incident}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
          },
        }
      );
      
      toast.success("Record deleted successfully", {
        position: "top-center",
        autoClose: 3000,
      });
      
      handleCloseDeleteModal();
      loadData();
    } catch (error) {
      console.error("Error deleting record:", error);
      toast.error(error.response?.data?.message || "Failed to delete record", {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // ADD MODAL HANDLERS
  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
    setAddValidationError("");
    setFormData({
      app_id: "",
      app_name: "",
      platform: "",
      portfolio: "",
      status: "Red",
      trigger_type: "",
      incident: "",
      priority: "",
      incident_dates: "",
      added_to_red_channel: DateTime.now(),
      moved_to_green_channel_on: null,
      final_review: DateTime.now().plus({ months: 3 }),
      deployment_type: "",
      deployment_details: "",
      comments: "",
    });
    setAppNameOptions([]);
    manuallySetDatesRef.current = {
      final_review: false,
      moved_to_green_channel_on: false,
    };
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  const handleFormChange = (field, value, isManualDateChange = false) => {
    if (isManualDateChange && (field === "final_review" || field === "moved_to_green_channel_on")) {
      manuallySetDatesRef.current[field] = true;
    }

    if (field === "added_to_red_channel" && value) {
      const addedDate = DateTime.isDateTime(value) ? value : DateTime.fromJSDate(value);
      const updates = { added_to_red_channel: addedDate };
      // Always update final_review when added_to_red_channel changes
      updates.final_review = addedDate.plus({ months: 3 });
      // Reset manual flags since we're auto-updating based on added_to_red_channel
      manuallySetDatesRef.current.final_review = false;
      setFormData((prev) => ({ ...prev, ...updates }));
      return;
    }

    if (field === "status") {
      if (value === "Green") {
        const updates = { status: value };
        if (!manuallySetDatesRef.current.moved_to_green_channel_on) {
          updates.moved_to_green_channel_on = DateTime.now();
        }
        setFormData((prev) => ({ ...prev, ...updates }));
      } else {
        setFormData((prev) => ({ ...prev, status: value, moved_to_green_channel_on: null }));
        manuallySetDatesRef.current.moved_to_green_channel_on = false;
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAppIdInput = (value) => {
    handleFormChange("app_id", value);
    if (!value || value.trim() === "") {
      setFormData((prev) => ({ ...prev, app_id: "", platform: "", portfolio: "", priority: "", incident_dates: "" }));
      setAppNameOptions([]);
    } else {
      setAppNameOptions([]);
    }
    if (appIdTimerRef.current) clearTimeout(appIdTimerRef.current);
    appIdTimerRef.current = setTimeout(async () => {
      if (value) {
        const data = await fetchAppDetailsByAppId(value);
        if (data) {
          setFormData((prev) => ({ ...prev, platform: data.platform || "", portfolio: data.portfolio || "" }));
          if (data.app_names && data.app_names.length > 0) {
            setAppNameOptions(data.app_names.map((name) => ({ label: name, value: name })));
          }
        }
      }
    }, 800);
  };

  const handleAppNameInput = (value) => {
    handleFormChange("app_name", value);
    if (appNameTimerRef.current) clearTimeout(appNameTimerRef.current);
    appNameTimerRef.current = setTimeout(async () => {
      if (value) {
        const data = await fetchAppDetailsByAppName(value);
        if (data) {
          setFormData((prev) => ({
            ...prev,
            app_id: data.app_id || prev.app_id,
            platform: data.platform || "",
            portfolio: data.portfolio || "",
          }));
        }
      }
    }, 800);
  };

  const handleAppNameSelected = (e) => {
    const selectedName = e.detail?.label || e.detail;
    setFormData((prev) => ({ ...prev, app_name: selectedName }));
  };

  const handleIncidentInput = (value) => {
    handleFormChange("incident", value);
    if (incidentTimerRef.current) clearTimeout(incidentTimerRef.current);
    incidentTimerRef.current = setTimeout(async () => {
      if (value) {
        const data = await fetchIncidentDetails(value);
        if (data) {
          setFormData((prev) => ({ ...prev, priority: data.priority || "", incident_dates: data.incident_date || "" }));
        }
      }
    }, 800);
  };

  const handleSubmitApplication = async () => {
    const missingFields = validateMandatoryFields(formData);
    if (missingFields.length > 0) {
      setAddValidationError(missingFields.join(", "));
      setAddScrollTrigger(prev => prev + 1);
      return;
    }

    setIsSubmitting(true);
    setAddValidationError("");
    try {
      await submitNewApplication(formData, graphData?.mail);
      toast.success("Application added successfully!", { position: "top-center", autoClose: 3000 });
      setIsAddModalOpen(false);
      await loadData();
    } catch (error) {
      console.error("Error adding application:", error);
      toast.error(error.response?.data?.message || "Failed to add to Red Channel", {
        position: "top-center",
        autoClose: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // EDIT MODAL HANDLERS
  const handleOpenEditModal = (rowData) => {
    const originalData = rowData._originalData;
    setOriginalAppName(originalData.app_name || "");
    setOriginalIncidentId(originalData.incident || "");
    setEditValidationError("");
    setEditFormData({
      app_id: originalData.app_id || "",
      app_name: originalData.app_name || "",
      platform: originalData.platform || "",
      portfolio: originalData.portfolio || "",
      status: originalData.status || "",
      trigger_type: originalData.trigger_type || "",
      incident: originalData.incident || "",
      priority: originalData.priority || "",
      incident_dates: originalData.incident_dates || "",
      added_to_red_channel: originalData.added_to_red_channel
        ? DateTime.fromISO(originalData.added_to_red_channel)
        : null,
      moved_to_green_channel_on: originalData.moved_to_green_channel_on
        ? DateTime.fromISO(originalData.moved_to_green_channel_on)
        : null,
      final_review: originalData.final_review ? DateTime.fromISO(originalData.final_review) : null,
      deployment_type: originalData.deployment_type || "",
      deployment_details: originalData.deployment_details || "",
      comments: originalData.comments || "",
    });
    setEditAppNameOptions([]);
    editManuallySetDatesRef.current = {
      final_review: !!originalData.final_review,
      moved_to_green_channel_on: !!originalData.moved_to_green_channel_on,
    };
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleEditFormChange = (field, value, isManualDateChange = false) => {
    if (isManualDateChange && (field === "final_review" || field === "moved_to_green_channel_on")) {
      editManuallySetDatesRef.current[field] = true;
    }

    if (field === "added_to_red_channel" && value) {
      const addedDate = DateTime.isDateTime(value) ? value : DateTime.fromJSDate(value);
      const updates = { added_to_red_channel: addedDate };
      // Always update final_review when added_to_red_channel changes
      updates.final_review = addedDate.plus({ months: 3 });
      // Reset manual flags since we're auto-updating based on added_to_red_channel
      editManuallySetDatesRef.current.final_review = false;
      setEditFormData((prev) => ({ ...prev, ...updates }));
      return;
    }

    if (field === "status") {
      if (value === "Green") {
        const updates = { status: value };
        if (!editManuallySetDatesRef.current.moved_to_green_channel_on) {
          updates.moved_to_green_channel_on = DateTime.now();
        }
        setEditFormData((prev) => ({ ...prev, ...updates }));
      } else {
        setEditFormData((prev) => ({ ...prev, status: value, moved_to_green_channel_on: null }));
        editManuallySetDatesRef.current.moved_to_green_channel_on = false;
      }
      return;
    }

    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditAppIdInput = (value) => {
    handleEditFormChange("app_id", value);
    if (!value || value.trim() === "") {
      setEditFormData((prev) => ({ ...prev, app_id: "", platform: "", portfolio: "", priority: "", incident_dates: "" }));
      setEditAppNameOptions([]);
    } else {
      setEditAppNameOptions([]);
    }
    if (editAppIdTimerRef.current) clearTimeout(editAppIdTimerRef.current);
    editAppIdTimerRef.current = setTimeout(async () => {
      if (value) {
        const data = await fetchAppDetailsByAppId(value);
        if (data) {
          setEditFormData((prev) => ({ ...prev, platform: data.platform || "", portfolio: data.portfolio || "" }));
          if (data.app_names && data.app_names.length > 0) {
            setEditAppNameOptions(data.app_names.map((name) => ({ label: name, value: name })));
          }
        }
      }
    }, 800);
  };

  const handleEditAppNameInput = (value) => {
    handleEditFormChange("app_name", value);
    if (editAppNameTimerRef.current) clearTimeout(editAppNameTimerRef.current);
    editAppNameTimerRef.current = setTimeout(async () => {
      if (value) {
        const data = await fetchAppDetailsByAppName(value);
        if (data) {
          setEditFormData((prev) => ({
            ...prev,
            app_id: data.app_id || prev.app_id,
            platform: data.platform || "",
            portfolio: data.portfolio || "",
          }));
        }
      }
    }, 800);
  };

  const handleEditAppNameSelected = (e) => {
    const selectedName = e.detail?.label || e.detail;
    setEditFormData((prev) => ({ ...prev, app_name: selectedName }));
  };

  const handleEditIncidentInput = (value) => {
    handleEditFormChange("incident", value);
    if (editIncidentTimerRef.current) clearTimeout(editIncidentTimerRef.current);
    editIncidentTimerRef.current = setTimeout(async () => {
      if (value) {
        const data = await fetchIncidentDetails(value);
        if (data) {
          setEditFormData((prev) => ({ ...prev, priority: data.priority || "", incident_dates: data.incident_date || "" }));
        }
      }
    }, 800);
  };

  const handleSubmitEditApplication = async () => {
    const missingFields = validateMandatoryFields(editFormData, true);
    if (missingFields.length > 0) {
      setEditValidationError(missingFields.join(", "));
      setEditScrollTrigger(prev => prev + 1);
      return;
    }

    setIsEditSubmitting(true);
    setEditValidationError("");
    try {
      await updateApplication(editFormData, graphData?.mail, originalAppName);
      toast.success("Application updated successfully!", { position: "top-center", autoClose: 3000 });
      setIsEditModalOpen(false);
      await loadData();
    } catch (error) {
      console.error("Error updating application:", error);
      toast.error(error.response?.data?.message || "Failed to update application", {
        position: "top-center",
        autoClose: 5000,
      });
    } finally {
      setIsEditSubmitting(false);
    }
  };

  // Helper function to get unique column values
  const getUniqueColumnValues = (data, columnId) => {
    if (!data || data.length === 0) return [];
    
    const values = [];
    let hasBlanks = false;
    
    data.forEach(item => {
      let value = item[columnId];
      if (value === null || value === undefined || value === '' || value === '-' || value === ' ') {
        hasBlanks = true;
      } else {
        values.push(String(value));
      }
    });
    
    const uniqueValues = [...new Set(values)].sort();
    if (hasBlanks) uniqueValues.push('[Blanks]');
    
    return uniqueValues;
  };

  // Helper function to apply column filters
  const applyColumnFilters = (data, filters) => {
    if (Object.keys(filters).length === 0) return data;

    return data.filter(item => {
      return Object.entries(filters).every(([columnId, selectedValues]) => {
        if (!Array.isArray(selectedValues) || selectedValues.length === 0) return true;
        
        let itemValue = item[columnId];
        let matchValue = '';
        
        if (itemValue === null || itemValue === undefined || itemValue === '' || itemValue === '-' || itemValue === ' ') {
          matchValue = '[Blanks]';
        } else {
          matchValue = String(itemValue);
        }
        
        return selectedValues.includes(matchValue);
      });
    });
  };

  // Unified column filter change handler
  const createFilterChangeHandler = (setFilterState) => (columnId, selectedValues) => {
    setFilterState(prev => {
      const newFilters = { ...prev };
      const valuesArray = Array.isArray(selectedValues) ? selectedValues : [];
      if (valuesArray.length === 0) {
        delete newFilters[columnId];
      } else {
        newFilters[columnId] = valuesArray;
      }
      return newFilters;
    });
  };

  const handlePlatformColumnFilterChange = createFilterChangeHandler(setPlatformColumnFilters);
  const handlePortfolioColumnFilterChange = createFilterChangeHandler(setPortfolioColumnFilters);
  const handleChannelColumnFilterChange = createFilterChangeHandler(setChannelColumnFilters);

  // Handle row selection for bidirectional filtering
  const handlePlatformRowClick = (platformName) => {
    if (selectedPlatform === platformName) {
      // Deselect if clicking the same platform
      setSelectedPlatform(null);
    } else {
      setSelectedPlatform(platformName);
      // Clear portfolio selection when platform is selected
      setSelectedPortfolio(null);
    }
  };

  const handlePortfolioRowClick = (portfolioName) => {
    if (selectedPortfolio === portfolioName) {
      // Deselect if clicking the same portfolio
      setSelectedPortfolio(null);
    } else {
      setSelectedPortfolio(portfolioName);
      // Clear platform selection when portfolio is selected
      setSelectedPlatform(null);
    }
  };

  // Render column filter function
  const renderColumnFilter = (columnId, label, dataKey, data, filterState, onFilterChange) => {
    const uniqueValues = getUniqueColumnValues(data, dataKey);
    const selectedValues = filterState[dataKey] || [];
    const isFiltered = selectedValues.length > 0;

    return (
      <div 
        slot={`${columnId}_header`} 
        style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '4px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <span>{label}</span>
        <McPopover 
          position="bottom-right" 
          trigger="click"
          click={(e) => e.stopPropagation()}
        >
          <McButton
            appearance="neutral"
            fit="small"
            hiddenlabel={true}
            icon={isFiltered ? "funnel-solid" : "funnel"}
            label="Show filter menu"
            padding="compact"
            slot="trigger"
            variant="plain"
            click={(e) => e.stopPropagation()}
          />
          <div 
            style={{ padding: '16px 12px', maxHeight: '300px', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            {uniqueValues.map((value, index) => (
              <McCheckbox
                key={`${columnId}-${index}`}
                label={value}
                value={value}
                checked={selectedValues.includes(value)}
                change={(e) => {
                  e.stopPropagation();
                  const isChecked = e.detail;
                  let newSelectedValues;
                  if (isChecked) {
                    newSelectedValues = [...selectedValues, value];
                  } else {
                    newSelectedValues = selectedValues.filter(v => v !== value);
                  }
                  onFilterChange(dataKey, newSelectedValues);
                }}
                style={{ display: 'block', marginBottom: '8px' }}
              />
            ))}
          </div>
        </McPopover>
      </div>
    );
  };

  // Data transformations with filters applied - optimized with proper memoization
  const channelTableColumns = useMemo(() => getChannelTableColumns(activeChannelTab), [activeChannelTab]);
  
  // Base data for filter dropdowns (excludes column filters to show all available options)
  const baseDataForFilters = useMemo(() => {
    let data = filterChannelData(redGreenChannelData, activeChannelTab);
    if (selectedPlatform) data = data.filter(item => item.platform === selectedPlatform);
    if (selectedPortfolio) data = data.filter(item => item.portfolio === selectedPortfolio);
    return data;
  }, [redGreenChannelData, activeChannelTab, selectedPlatform, selectedPortfolio]);
  
  // Apply all filters in optimized order: tab filter -> row selection -> column filters -> sort
  const filteredChannelData = useMemo(() => {
    let data = baseDataForFilters;
    
    // Apply column filters
    data = applyColumnFilters(data, channelColumnFilters);
    
    // Sort
    return sortArrayByProperty(data, sortColumn, sortDirection);
  }, [baseDataForFilters, channelColumnFilters, sortColumn, sortDirection]);
  
  // Transform for display (formatting dates, etc.)
  const transformedForDisplay = useMemo(() => {
    return filteredChannelData.map((item, index) => {
      const formatDate = (dateValue) => {
        if (!dateValue) return "-";
        const date = DateTime.fromISO(dateValue);
        return date.isValid ? date.toFormat("dd-MM-yyyy") : "-";
      };

      return {
        id: `channel-${item.app_id}-${index}`,
        app_id: item.app_id || "-",
        app_name: item.app_name || "-",
        platform: item.platform || "-",
        portfolio: item.portfolio || "-",
        status: item.status || "-",
        trigger_type: item.trigger_type || "-",
        latest_incident: item.latest_incident || "-",
        priority: item.priority || "-",
        latest_incident_date: item.latest_incident_date || "-",
        added_to_red_channel: formatDate(item.added_to_red_channel),
        moved_to_green_channel_on: formatDate(item.moved_to_green_channel_on),
        final_review: formatDate(item.final_review),
        deployment_type: item.deployment_type || "-",
        deployment_details: item.deployment_details || "-",
        comments: item.comments || "-",
        _originalData: item,
      };
    });
  }, [filteredChannelData]);

  
  // Base data for filter dropdowns - transformed version
  const baseDataForFiltersTransformed = useMemo(() => {
    return baseDataForFilters.map((item, index) => {
      const formatDate = (dateValue) => {
        if (!dateValue) return "-";
        const date = DateTime.fromISO(dateValue);
        return date.isValid ? date.toFormat("dd-MM-yyyy") : "-";
      };

      return {
        id: `channel-${item.app_id}-${index}`,
        app_id: item.app_id || "-",
        app_name: item.app_name || "-",
        platform: item.platform || "-",
        portfolio: item.portfolio || "-",
        status: item.status || "-",
        trigger_type: item.trigger_type || "-",
        latest_incident: item.latest_incident || "-",
        priority: item.priority || "-",
        latest_incident_date: item.latest_incident_date || "-",
        added_to_red_channel: formatDate(item.added_to_red_channel),
        moved_to_green_channel_on: formatDate(item.moved_to_green_channel_on),
        final_review: formatDate(item.final_review),
        deployment_type: item.deployment_type || "-",
        deployment_details: item.deployment_details || "-",
        comments: item.comments || "-",
        _originalData: item,
      };
    });
  }, [baseDataForFilters]);
  
  // Calculate unique app names count
  const uniqueAppNamesCount = useMemo(() => {
    const uniqueAppNames = new Set(transformedForDisplay.map(row => row.app_name));
    return uniqueAppNames.size;
  }, [transformedForDisplay]);
  
  const totalPages = Math.ceil(transformedForDisplay.length / RECORDS_PER_PAGE);
  const paginatedChannelData = useMemo(() => {
    return transformedForDisplay.slice(
      (currentPage - 1) * RECORDS_PER_PAGE,
      currentPage * RECORDS_PER_PAGE
    );
  }, [transformedForDisplay, currentPage]);

  const handlePageChange = (event) => {
    setCurrentPage(event.detail);
  };

  const handleTableSorted = ({ detail: { column, direction } }) => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  return (
    <ThemeProvider theme={darkPrimaryTheme}>
      <div className="home" style={showAnalytics ? { padding: "30px 40px", paddingBottom: "60px" } : {}}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
          <h1 style={{ fontWeight: "600", fontSize: "1.75rem", color: "#333", margin: 0 }}>
            Red/Green Channel Tracker
          </h1>
          <div style={{display: "flex", gap: "10px", alignItems: "center" }}>
                <McButton
                label="Refresh"
                appearance="neutral"
                variant="outlined"
                fit="medium"
                click={handleRefresh}
                disabled={loading}
                icon="arrow-clockwise"
              />
          {(userRole === "admin" || userRole === "change manager") && (
            <McButton
              label={addButtonLabel}
              appearance="primary"
              variant="filled"
              click={handleOpenAddModal}
              icon="plus"
              fit = "medium"
            />
          )}
          </div>
           
        </div>

        {/* Analytics Section */}
        {showAnalytics && (
          <AnalyticsSection
            redGreenAnalytics={redGreenAnalytics}
            loading={loading}
            onDownloadPlatform={handleDownloadPlatformAnalytics}
            onDownloadPortfolio={handleDownloadPortfolioAnalytics}
            renderPlatformColumnFilter={(columnId, label, dataKey, data) =>
              renderColumnFilter(columnId, label, dataKey, data, platformColumnFilters, handlePlatformColumnFilterChange)
            }
            renderPortfolioColumnFilter={(columnId, label, dataKey, data) =>
              renderColumnFilter(columnId, label, dataKey, data, portfolioColumnFilters, handlePortfolioColumnFilterChange)
            }
            platformColumnFilters={platformColumnFilters}
            portfolioColumnFilters={portfolioColumnFilters}
            applyColumnFilters={applyColumnFilters}
            selectedPlatform={selectedPlatform}
            selectedPortfolio={selectedPortfolio}
            onPlatformRowClick={handlePlatformRowClick}
            onPortfolioRowClick={handlePortfolioRowClick}
          />
        )}

        {/* Channel Tables Section */}
        <div style={{ marginTop: "40px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
            <div>
              <h2 style={{ fontSize: "1.5rem", fontWeight: "600", color: "#333", margin: 0 }}>
                Details
              </h2>
              {(selectedPlatform || selectedPortfolio) && (
                <div style={{ 
                  fontSize: "0.875rem", 
                  color: "#666", 
                  marginTop: "4px",
                  fontStyle: "italic"
                }}>
                  {selectedPlatform && `Showing rows for Platform: ${selectedPlatform}`}
                  {selectedPortfolio && `Showing rows for Portfolio: ${selectedPortfolio}`}
                </div>
              )}
            </div>
          </div>

          <McTabBar tabchange={handleChannelTabChange} currentindex={getChannelTabIndex()}>
            {CHANNEL_TAB_LABELS.map((label, index) => (
              <React.Fragment key={label}>
                <McTab slot="tab" label={label} active={activeChannelTab === label} />
                <div slot="panel">
                  <div style={{ marginTop: "20px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                      <span style={{ fontSize: "1rem", fontWeight: "600", color: label === "Red Channel" ? "#e75858" : label === "Moved to Green Channel" ? "#00786b" : "#0077b2" }}>
                        {label==='Red Channel' || label ==='Moved to Green Channel' ? (`Total ${label} App Instances: ${uniqueAppNamesCount}`) : (`Total App Instances: ${uniqueAppNamesCount}`)} 
                      </span>
                       <div style={{ display: "flex", gap: "10px" }}>
             
                      <McButton
                        label="Download"
                        appearance="primary"
                        variant="filled"
                        fit="small"
                        click={handleDownloadClick}
                        disabled={loading}
                      >
                        <span slot="trailingicon">
                          <mc-icon icon="tray-arrow-down" color="white"></mc-icon>
                        </span>
                      </McButton>
                    </div>
                    </div>
                    <McTable
                      data={paginatedChannelData}
                      columns={channelTableColumns}
                      datakey="id"
                      headersticky
                      fit="small"
                      sortmanual
                      sortchange={handleTableSorted}
                      sortdefaultcolumnid={sortColumn}
                      sortdefaultdirection={sortDirection}
                      customstyles=".mds-table { max-height: 60vh !important; }"
                      stateslotheight="200px"
                    >
                      {loading ? (
                        <mc-loading-indicator slot="state"></mc-loading-indicator>
                      ) : (
                        paginatedChannelData?.length === 0 && (
                          <div slot="state" style={{ textAlign: "center", padding: "2rem", fontSize: "1.5rem", fontWeight: "600" }}>
                            No Data Found
                          </div>
                        )
                      )}
                      {/* Column filters for channel tables */}
                      {renderColumnFilter('app_id', 'App ID', 'app_id', baseDataForFiltersTransformed, channelColumnFilters, handleChannelColumnFilterChange)}
                      {renderColumnFilter('app_name', 'App Instance Name', 'app_name', baseDataForFiltersTransformed, channelColumnFilters, handleChannelColumnFilterChange)}
                      {renderColumnFilter('platform', 'Platform', 'platform', baseDataForFiltersTransformed, channelColumnFilters, handleChannelColumnFilterChange)}
                      {renderColumnFilter('portfolio', 'Portfolio', 'portfolio', baseDataForFiltersTransformed, channelColumnFilters, handleChannelColumnFilterChange)}
                      {renderColumnFilter('trigger_type', 'Trigger', 'trigger_type', baseDataForFiltersTransformed, channelColumnFilters, handleChannelColumnFilterChange)}
                      {renderColumnFilter('incident', 'Incident', 'incident', baseDataForFiltersTransformed, channelColumnFilters, handleChannelColumnFilterChange)}
                      {renderColumnFilter('priority', 'Priority', 'priority', baseDataForFiltersTransformed, channelColumnFilters, handleChannelColumnFilterChange)}
                      {renderColumnFilter('deployment_type', 'Deployment Type', 'deployment_type', baseDataForFiltersTransformed, channelColumnFilters, handleChannelColumnFilterChange)}
                      {renderColumnFilter('deployment_details', 'Deployment Details', 'deployment_details', baseDataForFiltersTransformed, channelColumnFilters, handleChannelColumnFilterChange)}
                      {paginatedChannelData?.map((row) => (
                        <React.Fragment key={row.id}>
                          <div slot={`${row.id}_app_id`}>
                            {row.app_id && row.app_id !== "-" && row.app_id.trim() !== "" ? (
                              <a
                                href={`https://msa.maersk.io/asset_lookup?applicationId=${encodeURIComponent(row.app_id)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#0077b2", textDecoration: "underline", cursor: "pointer" }}
                              >
                                {row.app_id}
                              </a>
                            ) : (
                              <span>{row.app_id}</span>
                            )}
                          </div>
                          <div slot={`${row.id}_latest_incident`}>
                            {row.latest_incident && row.latest_incident !== "-" ? (
                              <a
                                href={`https://maersk.service-now.com/now/nav/ui/classic/params/target/incident.do%3Fsysparm_query%3Dnumber%3D${row.latest_incident}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#0077b2", textDecoration: "underline", cursor: "pointer" }}
                              >
                                {row.latest_incident}
                              </a>
                            ) : (
                              <span>{row.latest_incident}</span>
                            )}
                          </div>
                          <div slot={`${row.id}_status`}>
                            <mc-tag appearance={row.status === "Red" ? "error" : "success"} fit="small">
                              {row.status}
                            </mc-tag>
                          </div>
                          <div slot={`${row.id}_edit_action`}>
                            <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                              <McTooltip
                                opendelay={50}
                                appearance="neutral-default"
                                trigger="hover focus"
                              >
                                <McButton
                                  slot="trigger"
                                  fit="small"
                                  click={() => handleOpenStatisticsModal(row)}
                                  icon="file-eye"
                                  hiddenlabel={true}
                                  appearance="neutral"
                                  variant="outlined"
                                />
                                <span>Additional Details & Documents</span>
                              </McTooltip>
                              {(userRole === "admin" || userRole === "change manager") && (
                                <McTooltip
                                  opendelay={50}
                                  appearance="neutral-default"
                                  trigger="hover focus"
                                >
                                  <McButton
                                    slot="trigger"
                                    fit="small"
                                    click={() => handleOpenEditModal(row)}
                                    icon="pencil"
                                    hiddenlabel={true}
                                    appearance="neutral"
                                    variant="outlined"
                                  />
                                  <span>Edit Details</span>
                                </McTooltip>
                              )}
                              {(userRole === "admin" || userRole === "change manager") && (
                                <McTooltip
                                  opendelay={50}
                                  appearance="neutral-default"
                                  trigger="hover focus"
                                >
                                  <McButton
                                    slot="trigger"
                                    fit="small"
                                    click={() => handleOpenDeleteModal(row)}
                                    icon="trash"
                                    hiddenlabel={true}
                                    appearance="neutral"
                                    variant="outlined"
                                  />
                                  <span>Delete Record</span>
                                </McTooltip>
                              )}
                              {(userRole === "admin" || userRole === "change manager") && 
                               (activeChannelTab === "Red Channel" || activeChannelTab === "Moved to Green Channel" || activeChannelTab === "All") && (
                                <McTooltip
                                  opendelay={50}
                                  appearance="neutral-default"
                                  trigger="hover focus"
                                >
                                  <McButton
                                    slot="trigger"
                                    fit="small"
                                    click={() => handleOpenDocumentUploadModal(row)}
                                    icon="tray-arrow-up"
                                    hiddenlabel={true}
                                    appearance="neutral"
                                    variant="outlined"
                                  />
                                  <span>Upload Document</span>
                                </McTooltip>
                              )}
                              {(userRole === "admin" || userRole === "change manager") && 
                               activeChannelTab === "Red Channel" && (
                                <McTooltip
                                  opendelay={50}
                                  appearance="neutral-default"
                                  trigger="hover focus"
                                >
                                  <McButton
                                    slot="trigger"
                                    fit="small"
                                    click={() => handleOpenEmailModal(row)}
                                    icon="bell"
                                    hiddenlabel={true}
                                    appearance="neutral"
                                    variant="outlined"
                                  />
                                  <span>Send Email Notification</span>
                                </McTooltip>
                              )}
                            </div>
                          </div>
                        </React.Fragment>
                      ))}
                    </McTable>
                    {!loading && transformedForDisplay.length > RECORDS_PER_PAGE && (
                      <div style={{ paddingTop: "1rem", display: "flex", justifyContent: "center" }}>
                        <McPagination currentpage={currentPage} totalpages={totalPages} pagechange={handlePageChange} />
                      </div>
                    )}
                  </div>
                </div>
              </React.Fragment>
            ))}
          </McTabBar>
        </div>
        {/* Bottom spacing to prevent overlap with footer/banner */}
        <div style={{ height: "40px" }}></div>
      </div>

      {/* Add Application Modal */}
      <McModal
        heading="Add to Red Channel"
        dimension="large"
        padding="default"
        open={isAddModalOpen}
        closed={handleCloseAddModal}
        closing={handleCloseAddModal}
        zindex={999}
      >
        
        <ApplicationFormFields
          formData={formData}
          appNameOptions={appNameOptions}
          appNameTypeaheadRef={appNameTypeaheadRef}
          onAppIdInput={handleAppIdInput}
          onAppNameInput={handleAppNameInput}
          onAppNameSelected={handleAppNameSelected}
          onIncidentInput={handleIncidentInput}
          onFormChange={handleFormChange}
        />
        {addValidationError && (
          <div
            ref={addErrorRef}
            style={{ 
            padding: "12px", 
            marginTop: "16px",
            marginBottom: "16px", 
            backgroundColor: "#fef0f0", 
            border: "1px solid #f5c6cb", 
            borderRadius: "4px",
            color: "#721c24"
          }}>
            <strong>Please fill in the mandatory fields:</strong>
            <div style={{ marginTop: "4px" }}>{addValidationError}</div>
          </div>
        )}
        <McButton
          slot="secondaryAction"
          appearance="neutral"
          variant="outlined"
          dialogaction="cancel"
          fit="medium"
          disabled={isSubmitting}
          click={handleCloseAddModal}
        >
          Cancel
        </McButton>
        <McButton
          slot="primaryAction"
          appearance="primary"
          fit="medium"
          disabled={isSubmitting}
          loading={isSubmitting}
          click={handleSubmitApplication}
        >
          Add
        </McButton>
      </McModal>

      {/* Edit Application Modal */}
      <McModal
        heading="Edit Record"
        dimension="large"
        padding="default"
        open={isEditModalOpen}
        closed={handleCloseEditModal}
        closing={handleCloseEditModal}
        zindex={999}
      >
        
        <ApplicationFormFields
          formData={editFormData}
          appNameOptions={editAppNameOptions}
          appNameTypeaheadRef={editAppNameTypeaheadRef}
          onAppIdInput={handleEditAppIdInput}
          onAppNameInput={handleEditAppNameInput}
          onAppNameSelected={handleEditAppNameSelected}
          onIncidentInput={handleEditIncidentInput}
          onFormChange={handleEditFormChange}
          isEditMode={true}
        />
        {editValidationError && (
          <div
            ref={editErrorRef}
            style={{ 
            padding: "12px", 
            marginTop: "16px",
            marginBottom: "16px", 
            backgroundColor: "#fef0f0", 
            border: "1px solid #f5c6cb", 
            borderRadius: "4px",
            color: "#721c24"
          }}>
            <strong>Please fill in the mandatory fields:</strong>
            <div style={{ marginTop: "4px" }}>{editValidationError}</div>
          </div>
        )}
        <McButton
          slot="secondaryAction"
          appearance="neutral"
          variant="outlined"
          dialogaction="cancel"
          fit="medium"
          disabled={isEditSubmitting}
          click={handleCloseEditModal}
        >
          Cancel
        </McButton>
        <McButton
          slot="primaryAction"
          appearance="primary"
          fit="medium"
          disabled={isEditSubmitting}
          loading={isEditSubmitting}
          click={handleSubmitEditApplication}
        >
          Save
        </McButton>
      </McModal>

      {/* Statistics Modal */}
      <StatisticsModal
        isOpen={isStatisticsModalOpen}
        loading={statisticsLoading}
        selectedRow={selectedRowForStats}
        statisticsData={statisticsData}
        incidentCounts={incidentCounts}
        oldestIncidentDate={oldestIncidentDate}
        postmortemPreventiveMeasures={postmortemPreventiveMeasures}
        onClose={handleCloseStatisticsModal}
        userRole={userRole}
        userEmail={graphData?.mail}
      />

      {/* Document Upload Modal */}
      <DocumentUploadModal
        isOpen={isDocumentUploadModalOpen}
        appName={selectedRowForUpload?.app_name}
        recordStatus={selectedRowForUpload?.status}
        userEmail={graphData?.mail}
        onClose={handleCloseDocumentUploadModal}
        onUploadSuccess={handleDocumentUploadSuccess}
      />

      

      {/* Delete Confirmation Modal */}
      <McModal
        heading="Delete Record?"
        dimension="small"
        padding="default"
        open={isDeleteModalOpen}
        closed={handleCloseDeleteModal}
        closing={handleCloseDeleteModal}
        zindex={999}
        height={"40%"}
      >
        <div style={{ }}>
          {recordToDelete && (
            <div style={{ marginTop: "1rem", padding: "0.75rem", borderRadius: "4px" }}>
              <p style={{ margin: "0.25rem 0", fontSize: "0.75rem" }}><strong>App Instance Name:</strong> {recordToDelete.app_name}</p>
              <p style={{ margin: "0.25rem 0", fontSize: "0.75rem" }}><strong>Incident:</strong> {recordToDelete.latest_incident}</p>
            </div>
          )}
        </div>
        <McButton
          slot="secondaryAction"
          appearance="neutral"
          variant="outlined"
          dialogaction="cancel"
          fit="medium"
          disabled={isDeleting}
          click={handleCloseDeleteModal}
        >
          Cancel
        </McButton>
        <McButton
          slot="primaryAction"
          appearance="error"
          fit="medium"
          disabled={isDeleting}
          loading={isDeleting}
          click={handleConfirmDelete}
        >
          Delete
        </McButton>
      </McModal>

      {/* Email Notification Modal */}
      <McModal
        heading="Send Email Notification"
        dimension="large"
        padding="default"
        open={isEmailModalOpen}
        closed={handleCloseEmailModal}
        closing={handleCloseEmailModal}
        zindex={999}
      >
        <div style={{ maxHeight: "70vh", overflowY: "auto", overflowX: "hidden" }}>
          {isLoadingOwners ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
              <mc-loading-indicator></mc-loading-indicator>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" , paddingBottom: "1rem", paddingRight: "0.5rem"}}>
              {selectedRowForEmail && (
                <div style={{ padding: "0.75rem", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                  <p style={{ margin: "0.25rem 0", fontSize: "0.875rem" }}>
                    <strong>App Name:</strong> {selectedRowForEmail.app_name}
                  </p>
                  <p style={{ margin: "0.25rem 0", fontSize: "0.875rem" }}>
                    <strong>App ID:</strong> {selectedRowForEmail.app_id}
                  </p>
                  <p style={{ margin: "0.25rem 0", fontSize: "0.875rem" }}>
                    <strong>Platform:</strong> {selectedRowForEmail.platform}
                  </p>
                </div>
              )}

              {/* Include Attachment Checkbox - Only for Automated Deployments in Red Channel */}
              {selectedRowForEmail && 
               activeChannelTab === "Red Channel" && 
               selectedRowForEmail.deployment_type && 
               selectedRowForEmail.deployment_type.toLowerCase() === "automated" && (
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.5rem" }}>
                  <input
                    type="checkbox"
                    id="includeAttachment"
                    checked={emailFormData.includeAttachment}
                    onChange={(e) => handleEmailFormChange("includeAttachment", e.target.checked)}
                    style={{ cursor: "pointer" }}
                  />
                  <label 
                    htmlFor="includeAttachment" 
                    style={{ 
                      fontSize: "0.875rem", 
                      fontWeight: "500", 
                      color: "#333",
                      cursor: "pointer",
                      userSelect: "none"
                    }}
                  >
                    Include process communication attachment
                  </label>
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem"}}>
                <label style={{ fontSize: "0.875rem", fontWeight: "600", color: "#333" }}>
                  Recipients (comma-separated emails)
                </label>
                <textarea
                  value={emailFormData.recipients}
                  onChange={(e) => handleEmailFormChange("recipients", e.target.value)}
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    fontSize: "0.875rem",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontFamily: "inherit",
                    resize: "vertical",
                  }}
                  placeholder="Enter email addresses separated by commas"
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <label style={{ fontSize: "0.875rem", fontWeight: "600", color: "#333" }}>
                  Subject
                </label>
                <input
                  type="text"
                  value={emailFormData.subject}
                  onChange={(e) => handleEmailFormChange("subject", e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    fontSize: "0.875rem",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontFamily: "inherit",
                  }}
                  placeholder="Enter email subject"
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <label style={{ fontSize: "0.875rem", fontWeight: "600", color: "#333" }}>
                  Body
                </label>
                <textarea
                  ref={emailBodyTextareaRef}
                  value={emailFormData.body}
                  onChange={(e) => {
                    handleEmailFormChange("body", e.target.value);
                    // Auto-expand textarea and scroll into view
                    const target = e.target;
                    target.style.height = "auto";
                    target.style.height = Math.min(target.scrollHeight, 800) + "px";
                    setTimeout(() => {
                      emailBodyTextareaRef.current?.scrollIntoView({ 
                        behavior: "smooth", 
                        block: "end"
                      });
                    }, 0);
                  }}
                  rows={20}
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    fontSize: "0.875rem",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontFamily: "inherit",
                    resize: "none",
                    whiteSpace: "pre-wrap",
                    minHeight: "400px",
                    overflow: "auto",
                  }}
                  placeholder="Enter email message"
                />
              </div>

              
            </div>
          )}
        </div>
        <McButton
          slot="secondaryAction"
          appearance="neutral"
          variant="outlined"
          dialogaction="cancel"
          fit="medium"
          click={handleCloseEmailModal}
          disabled={isLoadingOwners || isSendingEmail}
        >
          Cancel
        </McButton>
        <McButton
          slot="primaryAction"
          appearance="primary"
          fit="medium"
          click={handleSendEmail}
          disabled={isLoadingOwners || isSendingEmail || !emailFormData.recipients || !emailFormData.subject || !emailFormData.body}
          loading={isSendingEmail}
        >
          Send Email
        </McButton>
      </McModal>
    </ThemeProvider>
  );
}
